import React, { useState, useEffect, useRef } from 'react';
import './Home.css';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { FaMapMarkerAlt, FaWalking, FaBus, FaBicycle, FaCar, FaRoute, FaPlus, FaSignOutAlt, FaInfoCircle } from 'react-icons/fa';
import Switch from 'react-switch';
import { initGoogleMaps } from '../../utils/googleMaps';

const Home = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isMobilePanelOpen, setIsMobilePanelOpen] = useState(false);
  const [disabilityAccess, setDisabilityAccess] = useState(false);
  const [frequency, setFrequency] = useState(50);
  const [tolerance, setTolerance] = useState(50); // 50 * 10 = 500 metres

  const [selectedTransport, setSelectedTransport] = useState(null);
  const [startLocation, setStartLocation] = useState('');
  const [endLocation, setEndLocation] = useState('');
  const [startCoordinates, setStartCoordinates] = useState(null);
  const [endCoordinates, setEndCoordinates] = useState(null);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const [mapsError, setMapsError] = useState(null);
  const [startLocationError, setStartLocationError] = useState('');
  const [endLocationError, setEndLocationError] = useState('');
  const [isStartLocationValid, setIsStartLocationValid] = useState(false);
  const [isEndLocationValid, setIsEndLocationValid] = useState(false);
  const [showDisabilityTooltip, setShowDisabilityTooltip] = useState(false);
  const [showFrequencyTooltip, setShowFrequencyTooltip] = useState(false);
  const [showToleranceTooltip, setShowToleranceTooltip] = useState(false);
  const [transportError, setTransportError] = useState('');
  
  // Journey date and time state
  const [journeyDate, setJourneyDate] = useState(() => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  });
  const [journeyTime, setJourneyTime] = useState(() => {
    const now = new Date();
    return now.toTimeString().slice(0, 5); // HH:MM format
  });

  const startInputRef = useRef(null);
  const endInputRef = useRef(null);
  const startAutocompleteRef = useRef(null);
  const endAutocompleteRef = useRef(null);

  const frequencyRef = useRef(null);
  const toleranceRef = useRef(null);


  useEffect(() => {
    // Initialize Google Maps
    initGoogleMaps()
      .then(() => {
        setIsGoogleMapsLoaded(true);
        setMapsError(null);
      })
      .catch((error) => {
        setTimeout(() => { throw new Error(`Error initializing Google Maps: ${error.message}`); }, 0);
        setMapsError(error.message);
      });
  }, []);

  useEffect(() => {
    if (isGoogleMapsLoaded && !mapsError && window.google) {
      try {
        // Initialize Google Places Autocomplete for start location
        if (startInputRef.current) {
          startAutocompleteRef.current = new window.google.maps.places.Autocomplete(startInputRef.current, {
            types: ['geocode', 'establishment'],
            componentRestrictions: { country: 'uk' }
          });
          startAutocompleteRef.current.addListener('place_changed', () => {
            const place = startAutocompleteRef.current.getPlace();
            if (place && place.formatted_address) {
              setStartLocation(place.formatted_address);
              setIsStartLocationValid(true);
              setStartLocationError('');
              
              // Extract coordinates from autocomplete to avoid geocoding
              if (place.geometry && place.geometry.location) {
                setStartCoordinates({
                  lat: place.geometry.location.lat(),
                  lng: place.geometry.location.lng()
                });
              }
            } else {
              setIsStartLocationValid(false);
              setStartLocationError('Please select a valid address from the dropdown menu');
              setStartCoordinates(null);
            }
          });
        }

        // Initialize Google Places Autocomplete for end location
        if (endInputRef.current) {
          endAutocompleteRef.current = new window.google.maps.places.Autocomplete(endInputRef.current, {
            types: ['geocode', 'establishment'],
            componentRestrictions: { country: 'uk' }
          });
          endAutocompleteRef.current.addListener('place_changed', () => {
            const place = endAutocompleteRef.current.getPlace();
            if (place && place.formatted_address) {
              setEndLocation(place.formatted_address);
              setIsEndLocationValid(true);
              setEndLocationError('');
              
              // Extract coordinates from autocomplete to avoid geocoding
              if (place.geometry && place.geometry.location) {
                setEndCoordinates({
                  lat: place.geometry.location.lat(),
                  lng: place.geometry.location.lng()
                });
              }
            } else {
              setIsEndLocationValid(false);
              setEndLocationError('Please select a valid address from the dropdown menu');
              setEndCoordinates(null);
            }
          });
        }
      } catch (error) {
        console.error('Error initializing Places Autocomplete:', error);
        setMapsError('Failed to initialize location search');
      }
    }
  }, [isGoogleMapsLoaded, mapsError]);

  // Set initial CSS properties for range sliders
  useEffect(() => {
    if (frequencyRef.current) {
      frequencyRef.current.style.setProperty('--range-progress', '50%');
    }
          if (toleranceRef.current) {
        // Set tolerance slider to 500 metres (50 out of 250 = 20%)
        toleranceRef.current.style.setProperty('--range-progress', '20%');
      }

  }, []);

  const inputContainerStyle = {
    width: window.innerWidth <= 768 ? '100%' : '50%',
    margin: '0 auto',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    zIndex: 1000,
    justifyContent: 'center',
  };

  const secondInputContainerStyle = {
    width: window.innerWidth <= 768 ? '100%' : '50%',
    margin: '0 auto',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    zIndex: 999,
    justifyContent: 'center',
  };

  const iconStyle = {
    position: 'absolute',
    left: '1rem',
    color: '#004C10',
    fontSize: '1.2rem',
    zIndex: 1001,
  };

  const inputStyle = {
    width: '100%',
    padding: '1rem 1rem 1rem 3rem',
    backgroundColor: '#f5f5f5',
    border: (props) => props.error ? '1px solid #ff0000' : '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '1rem',
    color: '#004C10',
    opacity: '1',
    transition: 'all 0.3s ease',
    fontFamily: '"Zen Old Mincho", serif',
    position: 'relative',
    zIndex: 1000,
  };

  const formStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: window.innerWidth <= 768 ? '1rem' : '2rem',
    padding: window.innerWidth <= 768 ? '0.5rem' : '2rem',
    maxWidth: window.innerWidth <= 768 ? '100%' : '800px',
    margin: '0 auto',
    fontFamily: '"Zen Old Mincho", serif',
    alignItems: 'center',
  };

  const sectionStyle = {
    width: window.innerWidth <= 768 ? '100%' : '50%',
    margin: '0 auto',
    padding: window.innerWidth <= 768 ? '0.75rem' : '1rem',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    color: '#004C10',
    fontSize: '1rem',
    fontWeight: '500',
    fontFamily: '"Zen Old Mincho", serif',
  };

  const controlContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '1.5rem',
  };

  const rangeStyle = {
    width: '100%',
    height: '4px',
    backgroundColor: '#e0e0e0',
    borderRadius: '2px',
    outline: 'none',
    WebkitAppearance: 'none',
    appearance: 'none',
    background: 'linear-gradient(to right, #004C10 0%, #004C10 var(--range-progress), #e0e0e0 var(--range-progress), #e0e0e0 100%)',
    '&::WebkitSliderThumb': {
      WebkitAppearance: 'none',
      appearance: 'none',
      width: '16px',
      height: '16px',
      backgroundColor: '#004C10',
      transform: 'rotate(45deg)',
      cursor: 'pointer',
      border: 'none',
      marginTop: '-6px',
    },
    '&::WebkitSliderRunnableTrack': {
      width: '100%',
      height: '4px',
      cursor: 'pointer',
      background: 'transparent',
      borderRadius: '2px',
      border: 'none',
    },
  };

  const transportSectionStyle = {
    width: window.innerWidth <= 768 ? '100%' : '50%',
    margin: '0 auto',
    padding: window.innerWidth <= 768 ? '0.75rem' : '1rem',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
  };

  const transportButtonsContainerStyle = {
    display: 'grid',
    gridTemplateColumns: window.innerWidth <= 768 ? 'repeat(2, 1fr)' : 'repeat(2, 1fr)',
    gap: window.innerWidth <= 768 ? '0.5rem' : '1rem',
    marginTop: '1rem',
    maxWidth: window.innerWidth <= 768 ? '100%' : '400px',
    margin: '1rem auto',
  };

  const transportButtonStyle = (isSelected) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: window.innerWidth <= 768 ? '0.75rem' : '1rem',
    backgroundColor: isSelected ? '#004C10' : '#f5f5f5',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    minWidth: '0',
    aspectRatio: window.innerWidth <= 768 ? '1.2' : '1.5',
    '&:hover': {
      backgroundColor: isSelected ? '#004C10' : '#e8e8e8',
    },
  });

  const transportIconStyle = (isSelected) => ({
    fontSize: window.innerWidth <= 768 ? '1.2rem' : '1.5rem',
    marginBottom: window.innerWidth <= 768 ? '0.5rem' : '0.75rem',
    color: isSelected ? '#fff' : '#004C10',
    flexShrink: 0,
  });

  const transportLabelStyle = (isSelected) => ({
    fontSize: window.innerWidth <= 768 ? '0.8rem' : '0.9rem',
    color: isSelected ? '#fff' : '#004C10',
    fontFamily: '"Zen Old Mincho", serif',
    textAlign: 'center',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    width: '100%',
  });

  const handleFocus = (e) => {
    e.target.style.opacity = '1';
  };

  const handleBlur = (e) => {
    e.target.style.opacity = '0.5';
  };

  const submitButtonContainerStyle = {
    width: window.innerWidth <= 768 ? '100%' : '50%',
    margin: window.innerWidth <= 768 ? '1rem auto' : '2rem auto',
    display: 'flex',
    justifyContent: 'center',
  };

  const submitButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: window.innerWidth <= 768 ? '0.5rem' : '0.75rem',
    padding: window.innerWidth <= 768 ? '0.75rem 1.5rem' : '1rem 2rem',
    backgroundColor: '#004C10',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: window.innerWidth <= 768 ? '1rem' : '1.1rem',
    fontFamily: '"Zen Old Mincho", serif',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    '&:hover': {
      backgroundColor: '#003a0c',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    },
  };

  const handleRangeChange = (e, setter) => {
    const value = e.target.value;
    const max = e.target.max;
    setter(value);
    // Calculate percentage based on the actual range
    const percentage = (value / max) * 100;
    e.target.style.setProperty('--range-progress', `${percentage}%`);
  };

  const handleTransportSelect = (transport) => {
    setSelectedTransport(transport);
    setTransportError(''); // Clear error when transport is selected
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setStartLocationError('');
    setEndLocationError('');
    setTransportError('');
    
    // Validate locations
    if (!isStartLocationValid) {
      setStartLocationError('Please select a valid start location from the dropdown menu');
      return;
    }
    if (!isEndLocationValid) {
      setEndLocationError('Please select a valid end location from the dropdown menu');
      return;
    }
    
    // Validate transport mode
    if (!selectedTransport) {
      setTransportError('Please select a transportation mode');
      return;
    }

    // If validation passes, proceed with form submission
    navigate('/route-results', { 
      state: {
        startLocation,
        endLocation,
        startCoordinates,
        endCoordinates,
        disabilityAccess,
        frequency,
        tolerance,
        journeyDate,
        journeyTime,
        selectedTransport,
      }
    });
  };

  const infoIconStyle = {
    color: '#004C10',
    fontSize: '1rem',
    marginLeft: '0.5rem',
    cursor: 'pointer',
    transition: 'color 0.3s ease',
  };

  const infoTooltipStyle = {
    position: 'absolute',
    backgroundColor: '#fff',
    border: '2px solid #004C10',
    borderRadius: '8px',
    padding: window.innerWidth <= 768 ? '0.5rem' : '0.75rem',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    zIndex: 1003,
    width: window.innerWidth <= 768 ? '200px' : '250px',
    fontSize: window.innerWidth <= 768 ? '0.8rem' : '0.85rem',
    color: '#004C10',
    fontWeight: '500',
    top: window.innerWidth <= 768 ? '100%' : '50%',
    left: window.innerWidth <= 768 ? '50%' : '100%',
    marginTop: window.innerWidth <= 768 ? '0.5rem' : '0',
    marginLeft: window.innerWidth <= 768 ? '0' : '0.5rem',
    // Animation properties
    opacity: 0,
    visibility: 'hidden',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    transform: window.innerWidth <= 768 ? 'translateX(-50%) translateY(-10px)' : 'translateY(-50%) translateX(-10px)',
  };

  const infoTooltipVisibleStyle = {
    ...infoTooltipStyle,
    opacity: 1,
    visibility: 'visible',
    transform: window.innerWidth <= 768 ? 'translateX(-50%) translateY(0)' : 'translateY(-50%) translateX(0)',
  };

  const toggleMobilePanel = () => {
    setIsMobilePanelOpen(!isMobilePanelOpen);
  };

  return (
    <div style={{ 
      padding: window.innerWidth <= 768 ? '0.5rem' : '2rem', 
      backgroundColor: '#f5f0f0', 
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      width: '100%',
      boxSizing: 'border-box',
      position: 'relative'
    }}>
      
      {/* Mobile Toggle Button - Only visible on mobile */}
      {window.innerWidth <= 768 && user && (
        <button
          onClick={toggleMobilePanel}
          style={{
            position: 'fixed',
            top: '1rem',
            right: '1rem',
            zIndex: 1003,
            backgroundColor: '#004C10',
            color: '#fff',
            border: 'none',
            borderRadius: '50%',
            width: '3rem',
            height: '3rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.2rem',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
          }}
        >
          {isMobilePanelOpen ? '✕' : '☰'}
        </button>
      )}

      {/* Mobile Backdrop - Only visible when panel is open */}
      {window.innerWidth <= 768 && isMobilePanelOpen && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1001,
          }}
          onClick={toggleMobilePanel}
        />
      )}

      {/* Mobile Navigation Panel - Only visible on mobile */}
      {window.innerWidth <= 768 && user && (
        <div style={{
          position: 'fixed',
          top: 0,
          right: isMobilePanelOpen ? '0' : '-85%',
          width: '85%',
          height: '100vh',
          backgroundColor: '#fff',
          zIndex: 1004,
          transition: 'right 0.3s ease',
          overflowY: 'auto',
          boxShadow: isMobilePanelOpen ? '0 0 20px rgba(0,0,0,0.3)' : 'none',
        }}>
          <div style={{
            padding: '1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
          }}>
            {/* Close Button */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
            }}>
              <h2 style={{
                color: '#004C10',
                fontSize: '1.2rem',
                fontFamily: '"Zen Old Mincho", serif',
                margin: 0,
              }}>
                Navigation
              </h2>
              <button
                onClick={toggleMobilePanel}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#004C10',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                ✕
              </button>
            </div>

            {/* Navigation Buttons */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
            }}>
              <Link
                to="/add-toilet"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem',
                  backgroundColor: '#004C10',
                  color: '#fff',
                  textDecoration: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontFamily: '"Zen Old Mincho", serif',
                  fontWeight: 'bold',
                  transition: 'all 0.2s ease',
                }}
                onClick={toggleMobilePanel}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#006622';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#004C10';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <FaPlus size={16} />
                Add Toilet
              </Link>
              
              <button
                onClick={() => {
                  logout();
                  toggleMobilePanel();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem',
                  backgroundColor: '#004C10',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontFamily: '"Zen Old Mincho", serif',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#006622';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#004C10';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <FaSignOutAlt size={16} />
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
      {mapsError && (
        <div style={{ 
          color: 'red', 
          textAlign: 'center', 
          marginBottom: '1rem',
          padding: '1rem',
          backgroundColor: '#fff',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          width: '100%',
          maxWidth: window.innerWidth <= 768 ? '100%' : '800px'
        }}>
          {mapsError}
        </div>
      )}
      <form style={{
        ...formStyle,
        width: '100%',
        maxWidth: window.innerWidth <= 768 ? '100%' : '800px'
      }} onSubmit={handleSubmit}>
        {/* Journey Date and Time Selector */}
        <div className="journey-selector">
          <label className="journey-question">
            When are you planning to travel?
          </label>
          <div className="date-time-inputs">
            <div className="input-group">
              <label>
                Date:
              </label>
              <input
                type="date"
                value={journeyDate}
                onChange={(e) => setJourneyDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="date-input"
              />
            </div>
            <div className="input-group">
              <label>
                Time:
              </label>
              <input
                type="time"
                value={journeyTime}
                onChange={(e) => setJourneyTime(e.target.value)}
                className="time-input"
              />
            </div>
          </div>
        </div>
        
        <div style={inputContainerStyle}>
          <FaMapMarkerAlt style={iconStyle} />
          <input
            ref={startInputRef}
            type="text"
            placeholder="Start"
            value={startLocation}
            onChange={(e) => {
              setStartLocation(e.target.value);
              // Only show error if user has stopped typing for a moment
              if (e.target.value.trim() === '') {
                setIsStartLocationValid(false);
                setStartLocationError('');
              }
            }}
            style={{...inputStyle, error: !!startLocationError}}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          {startLocationError && (
            <div style={{
              position: 'absolute',
              backgroundColor: '#fff',
              border: '2px solid #ff0000',
              borderRadius: '8px',
              padding: window.innerWidth <= 768 ? '0.5rem' : '0.75rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 1002,
              width: window.innerWidth <= 768 ? '250px' : '300px',
              fontSize: window.innerWidth <= 768 ? '0.8rem' : '0.85rem',
              color: '#ff0000',
              fontWeight: '500',
              top: window.innerWidth <= 768 ? '100%' : '50%',
              left: window.innerWidth <= 768 ? '50%' : '100%',
              transform: window.innerWidth <= 768 ? 'translateX(-50%)' : 'translateY(-50%)',
              marginTop: window.innerWidth <= 768 ? '0.5rem' : '0',
              marginLeft: window.innerWidth <= 768 ? '0' : '0.5rem',
            }}>
              {window.innerWidth <= 768 ? (
                // Mobile arrow - pointing up
                <>
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    borderLeft: '8px solid transparent',
                    borderRight: '8px solid transparent',
                    borderTop: '8px solid #ff0000',
                  }}></div>
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    borderLeft: '7px solid transparent',
                    borderRight: '7px solid transparent',
                    borderTop: '7px solid #fff',
                    marginTop: '1px',
                  }}></div>
                </>
              ) : (
                // Desktop arrow - pointing left
                <>
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '-8px',
                    transform: 'translateY(-50%)',
                    borderTop: '8px solid transparent',
                    borderBottom: '8px solid transparent',
                    borderRight: '8px solid #ff0000',
                  }}></div>
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '-6px',
                    transform: 'translateY(-50%)',
                    borderTop: '7px solid transparent',
                    borderBottom: '7px solid transparent',
                    borderRight: '7px solid #fff',
                  }}></div>
                </>
              )}
              {startLocationError}
            </div>
          )}
        </div>
        <div style={secondInputContainerStyle}>
          <FaMapMarkerAlt style={iconStyle} />
          <input
            ref={endInputRef}
            type="text"
            placeholder="Where are you going today?"
            value={endLocation}
            onChange={(e) => {
              setEndLocation(e.target.value);
              // Only show error if user has stopped typing for a moment
              if (e.target.value.trim() === '') {
                setIsEndLocationValid(false);
                setEndLocationError('');
              }
            }}
            style={{...inputStyle, error: !!endLocationError}}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          {endLocationError && (
            <div style={{
              position: 'absolute',
              backgroundColor: '#fff',
              border: '2px solid #ff0000',
              borderRadius: '8px',
              padding: window.innerWidth <= 768 ? '0.5rem' : '0.75rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 1002,
              width: window.innerWidth <= 768 ? '250px' : '350px',
              fontSize: window.innerWidth <= 768 ? '0.8rem' : '0.85rem',
              color: '#ff0000',
              fontWeight: '500',
              top: window.innerWidth <= 768 ? '100%' : '50%',
              left: window.innerWidth <= 768 ? '50%' : '100%',
              transform: window.innerWidth <= 768 ? 'translateX(-50%)' : 'translateY(-50%)',
              marginTop: window.innerWidth <= 768 ? '0.5rem' : '0',
              marginLeft: window.innerWidth <= 768 ? '0' : '0.5rem',
            }}>
              {window.innerWidth <= 768 ? (
                // Mobile arrow - pointing up
                <>
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    borderLeft: '8px solid transparent',
                    borderRight: '8px solid transparent',
                    borderTop: '8px solid #ff0000',
                  }}></div>
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    borderLeft: '7px solid transparent',
                    borderRight: '7px solid transparent',
                    borderTop: '7px solid #fff',
                    marginTop: '1px',
                  }}></div>
                </>
              ) : (
                // Desktop arrow - pointing left
                <>
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '-8px',
                    transform: 'translateY(-50%)',
                    borderTop: '8px solid transparent',
                    borderBottom: '8px solid transparent',
                    borderRight: '8px solid #ff0000',
                  }}></div>
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '-6px',
                    transform: 'translateY(-50%)',
                    borderTop: '7px solid transparent',
                    borderBottom: '7px solid transparent',
                    borderRight: '7px solid #fff',
                  }}></div>
                </>
              )}
              {endLocationError}
            </div>
          )}
        </div>

        <div style={sectionStyle}>
          <div style={{...controlContainerStyle, position: 'relative'}}>
            <div style={{display: 'flex', alignItems: 'center'}}>
              <label style={labelStyle}>Disability Access</label>
              <FaInfoCircle 
                style={infoIconStyle}
                onMouseEnter={() => setShowDisabilityTooltip(true)}
                onMouseLeave={() => setShowDisabilityTooltip(false)}
                onTouchStart={() => setShowDisabilityTooltip(true)}
                onTouchEnd={() => setTimeout(() => setShowDisabilityTooltip(false), 2000)}
              />
            </div>
            <Switch
              onChange={setDisabilityAccess}
              checked={disabilityAccess}
              onColor="#004C10"
              offColor="#e0e0e0"
              height={20}
              width={40}
              handleDiameter={16}
            />
                                    <div style={showDisabilityTooltip ? infoTooltipVisibleStyle : infoTooltipStyle}>
              {window.innerWidth <= 768 ? (
                // Mobile arrow - pointing up
                <>
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    borderLeft: '8px solid transparent',
                    borderRight: '8px solid transparent',
                    borderTop: '8px solid #004C10',
                  }}></div>
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    borderLeft: '7px solid transparent',
                    borderRight: '7px solid transparent',
                    borderTop: '7px solid #fff',
                    marginTop: '1px',
                  }}></div>
                </>
              ) : (
                // Desktop arrow - pointing left
                <>
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '-8px',
                    transform: 'translateY(-50%)',
                    borderTop: '8px solid transparent',
                    borderBottom: '8px solid transparent',
                    borderRight: '8px solid #004C10',
                  }}></div>
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '-6px',
                    transform: 'translateY(-50%)',
                    borderTop: '7px solid transparent',
                    borderBottom: '7px solid transparent',
                    borderRight: '7px solid #fff',
                  }}></div>
                </>
              )}
              When enabled, only loos with disability access facilities will be recommended for your route.
            </div>
          </div>



          <div style={{ marginBottom: '1.5rem', position: 'relative' }}>
            <div style={{display: 'flex', alignItems: 'center'}}>
              <label style={labelStyle}>Frequency (minutes between stops): 100 to 0</label>
              <FaInfoCircle 
                style={infoIconStyle}
                onMouseEnter={() => setShowFrequencyTooltip(true)}
                onMouseLeave={() => setShowFrequencyTooltip(false)}
                onTouchStart={() => setShowFrequencyTooltip(true)}
                onTouchEnd={() => setTimeout(() => setShowFrequencyTooltip(false), 2000)}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <input
                ref={frequencyRef}
                type="range"
                min="0"
                max="100"
                value={100 - frequency}
                onChange={e => {
                  const reversedValue = 100 - Number(e.target.value);
                  setFrequency(reversedValue);
                  e.target.style.setProperty('--range-progress', `${e.target.value}%`);
                }}
                style={rangeStyle}
              />
              <span style={{ minWidth: 32, fontWeight: 500, color: '#004C10' }}>{frequency}</span>
            </div>
            <div style={showFrequencyTooltip ? infoTooltipVisibleStyle : infoTooltipStyle}>
              {window.innerWidth <= 768 ? (
                // Mobile arrow - pointing up
                <>
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    borderLeft: '8px solid transparent',
                    borderRight: '8px solid transparent',
                    borderTop: '8px solid #004C10',
                  }}></div>
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    borderLeft: '7px solid transparent',
                    borderRight: '7px solid transparent',
                    borderTop: '7px solid #fff',
                    marginTop: '1px',
                  }}></div>
                </>
              ) : (
                // Desktop arrow - pointing left
                <>
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '-8px',
                    transform: 'translateY(-50%)',
                    borderTop: '8px solid transparent',
                    borderBottom: '8px solid transparent',
                    borderRight: '8px solid #004C10',
                  }}></div>
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '-6px',
                    transform: 'translateY(-50%)',
                    borderTop: '7px solid transparent',
                    borderBottom: '7px solid transparent',
                    borderRight: '7px solid #fff',
                  }}></div>
                </>
              )}
              Set how often you need to stop for loo breaks during your journey. Lower values mean more frequent stops. We will suggest stopping at no more than five locations along the way.
            </div>
          </div>

          <div style={{ marginBottom: '1.5rem', position: 'relative' }}>
            <div style={{display: 'flex', alignItems: 'center'}}>
              <label style={labelStyle}>Tolerance (maximum detour in meters): 0 to 2500</label>
              <FaInfoCircle 
                style={infoIconStyle}
                onMouseEnter={() => setShowToleranceTooltip(true)}
                onMouseLeave={() => setShowToleranceTooltip(false)}
                onTouchStart={() => setShowToleranceTooltip(true)}
                onTouchEnd={() => setTimeout(() => setShowToleranceTooltip(false), 2000)}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <input
                ref={toleranceRef}
                type="range"
                min="0"
                max="250"
                value={tolerance}
                onChange={(e) => handleRangeChange(e, setTolerance)}
                style={rangeStyle}
              />
              <span style={{ minWidth: 32, fontWeight: 500, color: '#004C10' }}>{tolerance * 10}</span>
            </div>
            <div style={showToleranceTooltip ? infoTooltipVisibleStyle : infoTooltipStyle}>
              {window.innerWidth <= 768 ? (
                // Mobile arrow - pointing up
                <>
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    borderLeft: '8px solid transparent',
                    borderRight: '8px solid transparent',
                    borderTop: '8px solid #004C10',
                  }}></div>
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    borderLeft: '7px solid transparent',
                    borderRight: '7px solid transparent',
                    borderTop: '7px solid #fff',
                    marginTop: '1px',
                  }}></div>
                </>
              ) : (
                // Desktop arrow - pointing left
                <>
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '-8px',
                    transform: 'translateY(-50%)',
                    borderTop: '8px solid transparent',
                    borderBottom: '8px solid transparent',
                    borderRight: '8px solid #004C10',
                  }}></div>
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '-6px',
                    transform: 'translateY(-50%)',
                    borderTop: '7px solid transparent',
                    borderBottom: '7px solid transparent',
                    borderRight: '7px solid #fff',
                  }}></div>
                </>
              )}
              Maximum distance you're willing to detour from your route to reach a loo. Higher values allow more distant loos to be included.
            </div>
          </div>
        </div>

        <div style={transportSectionStyle}>
          <label style={labelStyle}>Transportation Mode</label>
          <div style={transportButtonsContainerStyle}>
            <button
              type="button"
              onClick={() => handleTransportSelect('walking')}
              style={transportButtonStyle(selectedTransport === 'walking')}
            >
              <FaWalking style={transportIconStyle(selectedTransport === 'walking')} />
              <span style={transportLabelStyle(selectedTransport === 'walking')}>Walking</span>
            </button>
            <button
              type="button"
              onClick={() => handleTransportSelect('public')}
              style={transportButtonStyle(selectedTransport === 'public')}
            >
              <FaBus style={transportIconStyle(selectedTransport === 'public')} />
              <span style={transportLabelStyle(selectedTransport === 'public')}>Public Transport</span>
            </button>
            <button
              type="button"
              onClick={() => handleTransportSelect('cycling')}
              style={transportButtonStyle(selectedTransport === 'cycling')}
            >
              <FaBicycle style={transportIconStyle(selectedTransport === 'cycling')} />
              <span style={transportLabelStyle(selectedTransport === 'cycling')}>Cycling</span>
            </button>
            <button
              type="button"
              onClick={() => handleTransportSelect('car')}
              style={transportButtonStyle(selectedTransport === 'car')}
            >
              <FaCar style={transportIconStyle(selectedTransport === 'car')} />
              <span style={transportLabelStyle(selectedTransport === 'car')}>Car</span>
            </button>
          </div>
          {transportError && (
            <div style={{
              position: 'absolute',
              backgroundColor: '#fff',
              border: '2px solid #ff0000',
              borderRadius: '8px',
              padding: window.innerWidth <= 768 ? '0.5rem' : '0.75rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 1002,
              width: window.innerWidth <= 768 ? '250px' : '300px',
              fontSize: window.innerWidth <= 768 ? '0.8rem' : '0.85rem',
              color: '#ff0000',
              fontWeight: '500',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              marginTop: '0.5rem',
            }}>
              <div style={{
                position: 'absolute',
                bottom: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderTop: '8px solid #ff0000',
              }}></div>
              <div style={{
                position: 'absolute',
                bottom: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                borderLeft: '7px solid transparent',
                borderRight: '7px solid transparent',
                borderTop: '7px solid #fff',
                marginTop: '1px',
              }}></div>
              {transportError}
            </div>
          )}
        </div>

        <div style={submitButtonContainerStyle}>
          <button type="submit" style={submitButtonStyle}>
            <FaRoute style={{ fontSize: '1.2rem' }} />
            Find my route
          </button>
        </div>
      </form>
    </div>
  );
};

export default Home; 